import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Users, Truck, Phone, Mail, MapPin, Upload, Download, Video, Search, RefreshCw, Send, CheckSquare, Square, ChevronLeft, ChevronRight, Filter, X, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useWebRTCCall, CallInterface, CallButton } from '@/components/CallInterface';
import { BadgeIcons, VerificationStatus } from '@/components/DriverBadges';
import { ReviewSummary } from '@/components/ReviewsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FleetPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [truckDialogOpen, setTruckDialogOpen] = useState(false);
  const [importType, setImportType] = useState(null);
  const fileInputRef = React.useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Advanced Search
  const [citySearch, setCitySearch] = useState('');
  const [zipSearch, setZipSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [states, setStates] = useState([]);
  const [totalDrivers, setTotalDrivers] = useState(0);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [driversPerPage, setDriversPerPage] = useState(100);
  
  // Bulk Selection
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Bulk Email Modal
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);
  
  // WebRTC calling hook
  const dispatcherId = 'dispatcher-001';
  const dispatcherName = 'Dispatch Center';
  const {
    callState,
    currentCall,
    incomingCall,
    localStream,
    remoteStream,
    isMuted,
    isVideoOff,
    startCall,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo
  } = useWebRTCCall(dispatcherId, dispatcherName, 'dispatcher');
  
  const [driverForm, setDriverForm] = useState({
    name: '',
    phone: '',
    email: '',
    license_number: '',
    current_lat: '',
    current_lng: '',
    home_base: '',
  });

  const [truckForm, setTruckForm] = useState({
    truck_number: '',
    make: '',
    model: '',
    year: '',
    capacity_lbs: '',
    current_lat: '',
    current_lng: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params for driver search
      const skip = (currentPage - 1) * driversPerPage;
      let driverUrl = `${API}/drivers?limit=${driversPerPage}&skip=${skip}`;
      if (stateFilter) driverUrl += `&state=${stateFilter}`;
      if (citySearch) driverUrl += `&city=${encodeURIComponent(citySearch)}`;
      if (zipSearch) driverUrl += `&zip_code=${zipSearch}`;
      if (searchQuery) driverUrl += `&search=${encodeURIComponent(searchQuery)}`;
      
      const [driversRes, trucksRes, statesRes, countRes] = await Promise.all([
        axios.get(driverUrl),
        axios.get(`${API}/trucks`),
        axios.get(`${API}/drivers/states`),
        axios.get(`${API}/drivers/count?${stateFilter ? `state=${stateFilter}` : ''}${citySearch ? `&city=${encodeURIComponent(citySearch)}` : ''}`)
      ]);
      
      setDrivers(driversRes.data);
      setTrucks(trucksRes.data);
      setStates(statesRes.data || []);
      setTotalDrivers(countRes.data?.total || driversRes.data.length);
      setSelectedDrivers([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error fetching fleet data:', error);
      toast.error('Failed to fetch fleet data');
    } finally {
      setLoading(false);
    }
  }, [currentPage, driversPerPage, stateFilter, citySearch, zipSearch, searchQuery]);
  
  // Search by location
  const searchByLocation = async () => {
    setCurrentPage(1);
    fetchData();
  };
  
  const clearFilters = () => {
    setCitySearch('');
    setZipSearch('');
    setStateFilter('');
    setSearchQuery('');
    setCurrentPage(1);
    fetchData();
  };
  
  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedDrivers([]);
    } else {
      setSelectedDrivers(drivers.map(d => d.id || d.email));
    }
    setSelectAll(!selectAll);
  };
  
  const toggleSelectDriver = (driverId) => {
    if (selectedDrivers.includes(driverId)) {
      setSelectedDrivers(selectedDrivers.filter(id => id !== driverId));
    } else {
      setSelectedDrivers([...selectedDrivers, driverId]);
    }
  };
  
  // Bulk email send
  const sendBulkEmail = async () => {
    if (selectedDrivers.length === 0) {
      toast.error('Please select at least one driver');
      return;
    }
    if (!emailSubject || !emailBody) {
      toast.error('Please enter subject and message');
      return;
    }
    
    setSending(true);
    try {
      // Get selected driver emails
      const selectedEmails = drivers
        .filter(d => selectedDrivers.includes(d.id || d.email))
        .map(d => d.email)
        .filter(email => email && !email.includes('@tempus.local'));
      
      if (selectedEmails.length === 0) {
        toast.error('No valid email addresses in selection');
        setSending(false);
        return;
      }
      
      // Send emails in batch
      let successCount = 0;
      let failCount = 0;
      
      for (const email of selectedEmails) {
        try {
          await axios.post(`${API}/email/send-single`, {
            recipient_email: email,
            subject: emailSubject,
            html_content: emailBody.replace(/\n/g, '<br>')
          });
          successCount++;
        } catch (e) {
          failCount++;
        }
      }
      
      toast.success(`Sent ${successCount} emails${failCount > 0 ? `, ${failCount} failed` : ''}`);
      setBulkEmailOpen(false);
      setEmailSubject('');
      setEmailBody('');
      setSelectedDrivers([]);
      setSelectAll(false);
    } catch (error) {
      toast.error('Failed to send emails');
    } finally {
      setSending(false);
    }
  };
  
  // Quick copy emails
  const copySelectedEmails = () => {
    const emails = drivers
      .filter(d => selectedDrivers.includes(d.id || d.email))
      .map(d => d.email)
      .filter(email => email && !email.includes('@tempus.local'))
      .join(', ');
    
    navigator.clipboard.writeText(emails);
    toast.success(`Copied ${emails.split(',').length} emails to clipboard`);
  };
  
  // Pagination
  const totalPages = Math.ceil(totalDrivers / driversPerPage);
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDriverSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: driverForm.name,
        phone: driverForm.phone,
        email: driverForm.email,
        license_number: driverForm.license_number,
        current_location: {
          type: 'Point',
          coordinates: [parseFloat(driverForm.current_lng), parseFloat(driverForm.current_lat)]
        },
        home_base: driverForm.home_base,
      };

      await axios.post(`${API}/drivers`, payload);
      toast.success('Driver added successfully');
      setDriverDialogOpen(false);
      fetchData();
      setDriverForm({
        name: '',
        phone: '',
        email: '',
        license_number: '',
        current_lat: '',
        current_lng: '',
        home_base: '',
      });
    } catch (error) {
      console.error('Error creating driver:', error);
      toast.error('Failed to create driver');
    }
  };

  const handleTruckSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        truck_number: truckForm.truck_number,
        make: truckForm.make,
        model: truckForm.model,
        year: parseInt(truckForm.year),
        capacity_lbs: parseFloat(truckForm.capacity_lbs),
        current_location: {
          type: 'Point',
          coordinates: [parseFloat(truckForm.current_lng), parseFloat(truckForm.current_lat)]
        },
      };

      await axios.post(`${API}/trucks`, payload);
      toast.success('Truck added successfully');
      setTruckDialogOpen(false);
      fetchData();
      setTruckForm({
        truck_number: '',
        make: '',
        model: '',
        year: '',
        capacity_lbs: '',
        current_lat: '',
        current_lng: '',
      });
    } catch (error) {
      console.error('Error creating truck:', error);
      toast.error('Failed to create truck');
    }
  };

  const DriverCard = ({ driver }) => {
    const driverId = driver.id || driver.email;
    const isSelected = selectedDrivers.includes(driverId);
    const verificationBadges = driver.verification?.badges || [];
    const isVerified = driver.is_verified || verificationBadges.length > 0;
    
    return (
      <Card className={`glass-card hover:border-primary/30 transition-all duration-300 ${isSelected ? 'ring-2 ring-green-500' : ''}`} data-testid={`driver-card-${driver.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              {/* Selection Checkbox */}
              <button
                onClick={() => toggleSelectDriver(driverId)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  isSelected ? 'bg-green-500 border-green-500' : 'border-slate-500 hover:border-green-400'
                }`}
              >
                {isSelected && <CheckSquare className="w-4 h-4 text-white" />}
              </button>
              <div className="p-2 rounded-lg bg-primary/15">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-foreground">{driver.name}</p>
                  {isVerified && (
                    <Shield className="w-3.5 h-3.5 text-green-500" title="Verified" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{driver.city || driver.home_base}</p>
              </div>
            </div>
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-md ${
              driver.status === 'available' ? 'bg-emerald-500/15 text-emerald-500' :
              'bg-amber-500/15 text-amber-500'
            }`}>
              {driver.status}
            </span>
          </div>

          {/* Verification Badges */}
          {verificationBadges.length > 0 && (
            <div className="mb-2">
              <BadgeIcons badges={verificationBadges} size="xs" />
            </div>
          )}

          {/* Rating Summary */}
          {driver.email && (
            <div className="mb-2">
              <ReviewSummary email={driver.email} compact />
            </div>
          )}

          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-muted-foreground" />
              <a href={`tel:${driver.phone}`} className="text-blue-400 hover:underline">{driver.phone}</a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
              <a href={`mailto:${driver.email}`} className="text-blue-400 hover:underline truncate max-w-[200px]">{driver.email}</a>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between">
            <div className="flex gap-1">
              <a 
                href={`openphone://dial?number=${encodeURIComponent(driver.phone)}&action=call`}
                className="p-2 rounded bg-purple-600 hover:bg-purple-700 text-white"
                title="Call via Quo"
              >
                📱
              </a>
              <a 
                href={`tel:${driver.phone}`}
                className="p-2 rounded bg-green-600 hover:bg-green-700 text-white"
                title="Call Phone"
              >
                <Phone className="w-4 h-4" />
              </a>
              <a 
                href={`mailto:${driver.email}?subject=Load Opportunity - Tempus Dispatch`}
                className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
                title="Send Email"
              >
                <Mail className="w-4 h-4" />
              </a>
              <CallButton recipientId={driver.id} recipientName={driver.name} onStartCall={startCall} variant="video" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const TruckCard = ({ truck }) => (
    <Card className="glass-card hover:border-primary/30 transition-all duration-300" data-testid={`truck-card-${truck.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/15">
              <Truck className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{truck.truck_number}</p>
              <p className="text-xs font-mono text-muted-foreground">ID: {truck.id.substring(0, 8)}</p>
            </div>
          </div>
          <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-md ${
            truck.status === 'available' ? 'bg-emerald-500/15 text-emerald-500' :
            'bg-amber-500/15 text-amber-500'
          }`}>
            {truck.status}
          </span>
        </div>

        <div className="space-y-2">
          <div>
            <p className="text-xs text-muted-foreground">Make & Model</p>
            <p className="text-sm font-semibold text-foreground">{truck.make} {truck.model}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Year</p>
              <p className="text-sm font-mono font-semibold text-foreground">{truck.year}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Capacity</p>
              <p className="text-sm font-mono font-semibold text-foreground">{truck.capacity_lbs} lbs</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      {/* Call Interface Overlay */}
      <CallInterface 
        callState={callState}
        currentCall={currentCall}
        incomingCall={incomingCall}
        localStream={localStream}
        remoteStream={remoteStream}
        isMuted={isMuted}
        isVideoOff={isVideoOff}
        onAnswer={answerCall}
        onReject={rejectCall}
        onEnd={endCall}
        onToggleMute={toggleMute}
        onToggleVideo={toggleVideo}
      />
      
      <div className="p-6 space-y-6 fade-in" data-testid="fleet-page-container">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Fleet Management</h1>
            <p className="text-muted-foreground mt-2">Manage drivers and trucks</p>
          </div>
        </div>

        <Tabs defaultValue="drivers" className="space-y-6" data-testid="fleet-tabs">
          <TabsList className="glass">
            <TabsTrigger value="drivers" data-testid="tab-drivers">
              <Users className="w-4 h-4 mr-2" />
              Drivers ({drivers.length})
            </TabsTrigger>
            <TabsTrigger value="trucks" data-testid="tab-trucks">
              <Truck className="w-4 h-4 mr-2" />
              Trucks ({trucks.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="drivers" className="space-y-6">
            {/* Advanced Search Bar */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  {/* Quick Search */}
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-slate-400 text-xs mb-1 block">Quick Search (Name, Email, Phone)</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search drivers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchByLocation()}
                        className="pl-10 bg-slate-900 border-slate-600"
                        data-testid="quick-search-input"
                      />
                    </div>
                  </div>
                  <div className="w-40">
                    <Label className="text-slate-400 text-xs mb-1 block">City Name</Label>
                    <Input
                      placeholder="e.g., Vernon"
                      value={citySearch}
                      onChange={(e) => setCitySearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchByLocation()}
                      className="bg-slate-900 border-slate-600"
                      data-testid="city-search-input"
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-slate-400 text-xs mb-1 block">ZIP Code</Label>
                    <Input
                      placeholder="e.g., 90058"
                      value={zipSearch}
                      onChange={(e) => setZipSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchByLocation()}
                      className="bg-slate-900 border-slate-600"
                      data-testid="zip-search-input"
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-slate-400 text-xs mb-1 block">State</Label>
                    <select
                      value={stateFilter}
                      onChange={(e) => { setStateFilter(e.target.value); setCurrentPage(1); }}
                      className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-600 text-white"
                      data-testid="state-filter-select"
                    >
                      <option value="">All</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <Button 
                    onClick={searchByLocation} 
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="search-location-btn"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                  <Button 
                    onClick={clearFilters} 
                    variant="outline"
                    className="border-slate-600"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Results Info & Bulk Actions */}
                <div className="flex flex-wrap items-center justify-between mt-4 pt-4 border-t border-slate-700">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-slate-400">
                      <span className="text-white font-bold">{totalDrivers.toLocaleString()}</span> drivers found
                      {(citySearch || stateFilter) && ` in ${citySearch ? citySearch + ', ' : ''}${stateFilter || 'All States'}`}
                    </p>
                    <div className="flex items-center gap-2">
                      <Label className="text-slate-400 text-xs">Per page:</Label>
                      <select
                        value={driversPerPage}
                        onChange={(e) => { setDriversPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        className="h-8 px-2 rounded bg-slate-900 border border-slate-600 text-white text-sm"
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={250}>250</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Bulk Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleSelectAll}
                      className="border-slate-600"
                    >
                      {selectAll ? <CheckSquare className="w-4 h-4 mr-2" /> : <Square className="w-4 h-4 mr-2" />}
                      {selectAll ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedDrivers.length > 0 && (
                      <>
                        <span className="text-sm text-green-400">{selectedDrivers.length} selected</span>
                        <Button
                          size="sm"
                          onClick={copySelectedEmails}
                          variant="outline"
                          className="border-slate-600"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Copy Emails
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setBulkEmailOpen(true)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Email Selected
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-slate-600"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-slate-400 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-slate-600"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* Bulk Email Dialog */}
            <Dialog open={bulkEmailOpen} onOpenChange={setBulkEmailOpen}>
              <DialogContent className="max-w-2xl glass-card">
                <DialogHeader>
                  <DialogTitle>Email {selectedDrivers.length} Drivers</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Subject</Label>
                    <Input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="e.g., 🔥 WEEKLY FLATBED WORK - Vernon CA"
                      className="bg-slate-900 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Enter your message to drivers..."
                      className="w-full h-48 p-3 rounded-md bg-slate-900 border border-slate-600 text-white"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setBulkEmailOpen(false)}>Cancel</Button>
                    <Button onClick={sendBulkEmail} disabled={sending} className="bg-green-600 hover:bg-green-700">
                      {sending ? 'Sending...' : `Send to ${selectedDrivers.length} Drivers`}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <div className="flex justify-end">
              <Dialog open={driverDialogOpen} onOpenChange={setDriverDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="glow-button" data-testid="add-driver-dialog-trigger">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Driver
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl glass-card">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading">Add New Driver</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleDriverSubmit} className="space-y-4 mt-4" data-testid="add-driver-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name</Label>
                        <Input
                          value={driverForm.name}
                          onChange={(e) => setDriverForm({...driverForm, name: e.target.value})}
                          required
                          data-testid="input-driver-name"
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          type="tel"
                          value={driverForm.phone}
                          onChange={(e) => setDriverForm({...driverForm, phone: e.target.value})}
                          required
                          data-testid="input-driver-phone"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={driverForm.email}
                          onChange={(e) => setDriverForm({...driverForm, email: e.target.value})}
                          required
                          data-testid="input-driver-email"
                        />
                      </div>
                      <div>
                        <Label>License Number</Label>
                        <Input
                          value={driverForm.license_number}
                          onChange={(e) => setDriverForm({...driverForm, license_number: e.target.value})}
                          required
                          data-testid="input-driver-license"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Home Base</Label>
                      <Input
                        value={driverForm.home_base}
                        onChange={(e) => setDriverForm({...driverForm, home_base: e.target.value})}
                        required
                        data-testid="input-driver-home-base"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Current Latitude</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="40.7128"
                          value={driverForm.current_lat}
                          onChange={(e) => setDriverForm({...driverForm, current_lat: e.target.value})}
                          required
                          data-testid="input-driver-lat"
                        />
                      </div>
                      <div>
                        <Label>Current Longitude</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="-74.0060"
                          value={driverForm.current_lng}
                          onChange={(e) => setDriverForm({...driverForm, current_lng: e.target.value})}
                          required
                          data-testid="input-driver-lng"
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full glow-button" data-testid="submit-driver-button">
                      Add Driver
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading drivers...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {drivers.map((driver) => (
                  <DriverCard key={driver.id} driver={driver} />
                ))}
              </div>
            )}

            {!loading && drivers.length === 0 && (
              <Card className="glass-card" data-testid="no-drivers-message">
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-heading text-foreground mb-2">No Drivers Yet</h3>
                  <p className="text-sm text-muted-foreground mb-6">Add your first driver to get started</p>
                  <Button onClick={() => setDriverDialogOpen(true)} className="glow-button" data-testid="add-first-driver-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Driver
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="trucks" className="space-y-6">
            <div className="flex justify-end">
              <Dialog open={truckDialogOpen} onOpenChange={setTruckDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="glow-button" data-testid="add-truck-dialog-trigger">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Truck
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl glass-card">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading">Add New Truck</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleTruckSubmit} className="space-y-4 mt-4" data-testid="add-truck-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Truck Number</Label>
                        <Input
                          value={truckForm.truck_number}
                          onChange={(e) => setTruckForm({...truckForm, truck_number: e.target.value})}
                          required
                          data-testid="input-truck-number"
                        />
                      </div>
                      <div>
                        <Label>Capacity (lbs)</Label>
                        <Input
                          type="number"
                          value={truckForm.capacity_lbs}
                          onChange={(e) => setTruckForm({...truckForm, capacity_lbs: e.target.value})}
                          required
                          data-testid="input-truck-capacity"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Make</Label>
                        <Input
                          value={truckForm.make}
                          onChange={(e) => setTruckForm({...truckForm, make: e.target.value})}
                          required
                          data-testid="input-truck-make"
                        />
                      </div>
                      <div>
                        <Label>Model</Label>
                        <Input
                          value={truckForm.model}
                          onChange={(e) => setTruckForm({...truckForm, model: e.target.value})}
                          required
                          data-testid="input-truck-model"
                        />
                      </div>
                      <div>
                        <Label>Year</Label>
                        <Input
                          type="number"
                          value={truckForm.year}
                          onChange={(e) => setTruckForm({...truckForm, year: e.target.value})}
                          required
                          data-testid="input-truck-year"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Current Latitude</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="40.7128"
                          value={truckForm.current_lat}
                          onChange={(e) => setTruckForm({...truckForm, current_lat: e.target.value})}
                          required
                          data-testid="input-truck-lat"
                        />
                      </div>
                      <div>
                        <Label>Current Longitude</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="-74.0060"
                          value={truckForm.current_lng}
                          onChange={(e) => setTruckForm({...truckForm, current_lng: e.target.value})}
                          required
                          data-testid="input-truck-lng"
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full glow-button" data-testid="submit-truck-button">
                      Add Truck
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading trucks...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {trucks.map((truck) => (
                  <TruckCard key={truck.id} truck={truck} />
                ))}
              </div>
            )}

            {!loading && trucks.length === 0 && (
              <Card className="glass-card" data-testid="no-trucks-message">
                <CardContent className="p-12 text-center">
                  <Truck className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-heading text-foreground mb-2">No Trucks Yet</h3>
                  <p className="text-sm text-muted-foreground mb-6">Add your first truck to get started</p>
                  <Button onClick={() => setTruckDialogOpen(true)} className="glow-button" data-testid="add-first-truck-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Truck
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default FleetPage;